import React, { useState, useEffect } from 'react';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Label } from '@/components/shadcn-ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/shadcn-ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn-ui/select';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { UserPlus, Trash2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { useUserStore } from '@/stores/useUser.store';

const AddAdmin = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ fullname: '', user_email: '', password: '' });
    const [errors, setErrors] = useState({ fullname: '', user_email: '', password: '' });
    const [submitting, setSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState(null);
    const [transferToAdminId, setTransferToAdminId] = useState('');
    const adminDetails = useUserStore(state => state.adminDetails);

    const fetchAdmins = async (page = 1) => {
        setLoading(true);
        try {
            const response = await appAxios.get(`/Admin/v1.0/admin-list?page=${page}&limit=10`);
            const filteredAdmins = response.data.data.data.filter(admin => admin.user_email !== adminDetails.user_email);
            setAdmins(filteredAdmins);
            setPagination(response.data.data.pagination);
        } catch (error) {
            toast.error('Failed to fetch admin list');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);


    const validateFullname = (name) => {
        if (!name.trim()) {
            return 'Full name is required';
        }
        if (name.trim().length < 3) {
            return 'Full name must be at least 3 characters';
        }
        return '';
    };

    const validateEmail = (email) => {
        if (!email.trim()) {
            return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address';
        }
        return '';
    };

    const validatePassword = (password) => {
        if (!password) {
            return 'Password is required';
        }
        if (password.length < 8) {
            return 'Password must be at least 8 characters';
        }
        if (!/[A-Z]/.test(password)) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(password)) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(password)) {
            return 'Password must contain at least one number';
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            return 'Password must contain at least one special character';
        }
        return '';
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors({ ...errors, [field]: '' });
        }
    };

    const handleBlur = (field) => {
        let error = '';
        if (field === 'fullname') {
            error = validateFullname(formData.fullname);
        } else if (field === 'user_email') {
            error = validateEmail(formData.user_email);
        } else if (field === 'password') {
            error = validatePassword(formData.password);
        }
        setErrors({ ...errors, [field]: error });
    };

    const validateForm = () => {
        const fullnameError = validateFullname(formData.fullname);
        const emailError = validateEmail(formData.user_email);
        const passwordError = validatePassword(formData.password);

        setErrors({
            fullname: fullnameError,
            user_email: emailError,
            password: passwordError,
        });

        return !fullnameError && !emailError && !passwordError;
    };



    const handleAddAdmin = async (e) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            toast.error('Please fix the validation errors');
            return;
        }

        setSubmitting(true);
        try {
            await appAxios.post('/Admin/v1.0/add-admin', formData);
            toast.success('Admin added successfully');
            setIsModalOpen(false);
            setFormData({ fullname: '', user_email: '', password: '' });
            setErrors({ fullname: '', user_email: '', password: '' });
            fetchAdmins();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to add admin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleModalClose = (open) => {
        setIsModalOpen(open);
        if (!open) {
            // Reset form and errors when closing
            setFormData({ fullname: '', user_email: '', password: '' });
            setErrors({ fullname: '', user_email: '', password: '' });
        }
    };

    const handleDeleteClick = (admin) => {
        setAdminToDelete(admin);
        setTransferToAdminId('');
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!adminToDelete) return;

        if (!transferToAdminId) {
            toast.error('Please select an admin to transfer permits to');
            return;
        }

        try {
            await appAxios.delete(`/Admin/v1.0/delete-admin/${adminToDelete._id}`, {
                data: { assignPermitsToAdminId: transferToAdminId }
            });
            toast.success('Admin deleted successfully');
            setDeleteDialogOpen(false);
            setAdminToDelete(null);
            setTransferToAdminId('');
            fetchAdmins(pagination?.page || 1);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to delete admin');
        }
    };

    const columns = [
        { key: 'fullname', label: 'Full Name' },
        { key: 'user_email', label: 'Email' },
        {
            key: 'createdAt',
            label: 'Created At',
            render: (value) => new Date(value).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }),
        },
        {
            key: '_id',
            label: 'Actions',
            render: (value, row) => (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(row);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Admin Management</h1>
                    {/* <p className="text-muted-foreground">Manage admin users and permissions</p> */}
                </div>
                <Button onClick={() => setIsModalOpen(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add Admin
                </Button>
            </div>

            <DataTable
                data={admins}
                columns={columns}
                loading={loading}
                pagination={pagination}
                onPageChange={fetchAdmins}
                emptyMessage="No admins found"
            />

            <Dialog open={isModalOpen} onOpenChange={handleModalClose}>
                <DialogContent
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>Add New Admin</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddAdmin}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="fullname">Full Name</Label>
                                <Input
                                    id="fullname"
                                    value={formData.fullname}
                                    onChange={(e) => handleInputChange('fullname', e.target.value)}
                                    onBlur={() => handleBlur('fullname')}
                                    className={errors.fullname ? 'border-red-500' : ''}
                                />
                                {errors.fullname && (
                                    <p className="text-sm text-red-500">{errors.fullname}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.user_email}
                                    onChange={(e) => handleInputChange('user_email', e.target.value)}
                                    onBlur={() => handleBlur('user_email')}
                                    className={errors.user_email ? 'border-red-500' : ''}
                                />
                                {errors.user_email && (
                                    <p className="text-sm text-red-500">{errors.user_email}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    onBlur={() => handleBlur('password')}
                                    className={errors.password ? 'border-red-500' : ''}
                                />
                                {errors.password && (
                                    <p className="text-sm text-red-500">{errors.password}</p>
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => handleModalClose(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting ? 'Adding...' : 'Add Admin'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Admin</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete <strong>{adminToDelete?.fullname}</strong> ({adminToDelete?.user_email})?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="transfer-admin">Transfer permits to</Label>
                            <Select value={transferToAdminId} onValueChange={setTransferToAdminId}>
                                <SelectTrigger id="transfer-admin">
                                    <SelectValue placeholder="Select an admin" />
                                </SelectTrigger>
                                <SelectContent>
                                    {admins
                                        .filter(admin => admin._id !== adminToDelete?._id).length > 0 ? (
                                        admins
                                            .filter(admin => admin._id !== adminToDelete?._id)
                                            .map(admin => (
                                                <SelectItem key={admin._id} value={admin._id}>
                                                    {admin.fullname} ({admin.user_email})
                                                </SelectItem>
                                            ))) : (<SelectItem
                                                disabled
                                                value="no-admins"
                                                className="text-center text-sm text-red-700 whitespace-normal py-3 px-2 leading-relaxed"
                                            >
                                                No admins available to transfer permits to.
                                                Please add an admin first.
                                            </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="button" variant="destructive" onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AddAdmin;

